import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Copy, Check, X, Plus, ChevronDown, ChevronRight, Loader2, AlertTriangle, Undo2, Sparkles } from 'lucide-react';
import InlineField from '../components/InlineField';
import { useUsersLookup } from '../api/queries';
import { useToast } from '../context/ToastContext';
import { copyReport, subjectFor, titleFor, companyFor, longDate, customerLeak } from './meetingReport';

// The meeting write-up, as a card: the report the person will send, laid out
// as it will read, every part editable in place. Copy puts it on the
// clipboard as rich and plain text; Approve saves the Meeting, the new
// people, the action items and who attended — through the ordinary routes,
// via onApprove. The assistant's own revisions arrive as a new card; this
// one folds away when that happens.

const newKey = (prefix) => `${prefix}${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

function Person({ person, side, onEdit, onToggle, canEdit, titled = true }) {
  const off = person.include === false;
  const matched = !person.is_new;
  const href = side === 'customer' && person.contact_id ? `/contacts/${person.contact_id}` : null;
  return (
    <li className={`mr-chip ${off ? 'is-off' : ''} ${person.is_new ? 'is-new' : ''}`}>
      {matched ? (
        <span className="mr-chip-name">{href ? <Link to={href}>{person.name}</Link> : person.name}</span>
      ) : (
        <span className="mr-chip-name"><InlineField value={person.name} onSave={(v) => onEdit('name', v)} label="Name" canEdit={canEdit && !off} /></span>
      )}
      {titled && (person.title || person.is_new) && (
        <span className="mr-chip-title">
          {person.is_new ? <InlineField value={person.title} onSave={(v) => onEdit('title', v)} label="Title" canEdit={canEdit && !off} emptyText="add title" placeholder="Title" /> : person.title}
        </span>
      )}
      {person.is_new && !off && <span className="mr-badge" title="Will be added on approval">new</span>}
      {canEdit && (
        <button type="button" className="mr-chip-x" onClick={onToggle} aria-label={off ? `Put ${person.name} back` : `Leave ${person.name} out`} title={off ? 'Put back' : 'Leave out'}>
          {off ? <Undo2 size={12} /> : <X size={12} />}
        </button>
      )}
    </li>
  );
}

function Adder({ placeholder, onAdd, label }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const commit = () => { const v = text.trim(); if (v) onAdd(v); setText(''); setOpen(false); };
  if (!open) return <button type="button" className="mr-add" onClick={() => setOpen(true)}><Plus size={12} aria-hidden="true" /> {label}</button>;
  return (
    <span className="mr-adder">
      <input
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setText(''); setOpen(false); } }}
        onBlur={commit}
        placeholder={placeholder}
        aria-label={label}
      />
    </span>
  );
}

export default function MeetingReportCard({ proposal, superseded = false, busy = false, onApprove, onDiscard, onAsk, onChange }) {
  const toast = useToast();
  // Edits are handed up as they are made (onChange), so closing the drawer
  // or reloading the page brings the card back as the person left it.
  const [report, setReport] = useState(proposal.draftPayload || proposal.payload);
  const firstRender = useRef(true);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (onChangeRef.current) onChangeRef.current(report);
  }, [report]);
  const [open, setOpen] = useState(true);
  const [working, setWorking] = useState(false);
  const baseRef = useRef(`mr-${proposal.id || proposal.key}-${Date.now().toString(36)}`);
  const pending = proposal.state === 'pending';
  const canEdit = pending && !busy && !working;
  const { data: users = [] } = useUsersLookup(pending);
  const leak = report.audience === 'customer' ? customerLeak(report) : null;
  const folded = superseded ? !open || open === 'auto' : !open;

  const patch = (fn) => setReport((r) => fn({ ...r }));
  const setList = (name, fn) => patch((r) => ({ ...r, [name]: fn([...(r[name] || [])]) }));
  const editAt = (name, i) => (field, value) => setList(name, (list) => { list[i] = { ...list[i], [field]: value }; return list; });
  const toggleAt = (name, i) => () => setList(name, (list) => { list[i] = { ...list[i], include: list[i].include === false }; return list; });
  const removeAt = (name, i) => () => setList(name, (list) => { list.splice(i, 1); return list; });

  const customers = report.customer_attendees || [];
  const internals = report.internal_attendees || [];
  const highlights = report.highlights || [];
  const steps = report.next_steps || [];
  const counts = {
    contacts: customers.filter((p) => p.include !== false && p.is_new).length,
    internal: internals.filter((p) => p.include !== false && p.is_new).length,
    tasks: steps.filter((n) => n.as_task !== false).length,
  };

  const copy = async () => {
    try {
      const how = await copyReport(report);
      toast.success(how === 'rich' ? 'Copied — paste it into your email' : 'Copied as plain text');
    } catch (e) { toast.error('Could not copy from here'); }
  };
  const copySubject = async () => {
    try { await navigator.clipboard.writeText(subjectFor(report)); toast.success('Subject copied'); } catch (e) { toast.error('Could not copy from here'); }
  };
  const approve = async () => {
    setWorking(true);
    try { await onApprove(report, baseRef.current); } finally { setWorking(false); }
  };
  const ownerValue = (n) => (n.owner_user_id ? `u${n.owner_user_id}` : n.owner_name ? `o:${n.owner_name}` : '');
  const setOwner = (i) => (e) => {
    const v = e.target.value;
    setList('next_steps', (list) => {
      const n = { ...list[i] };
      if (v.startsWith('u')) { const u = users.find((x) => String(x.id) === v.slice(1)); n.owner_user_id = u ? u.id : null; n.owner_name = u ? u.name : ''; }
      else if (v.startsWith('o:')) { n.owner_user_id = null; n.owner_name = v.slice(2); }
      else { n.owner_user_id = null; n.owner_name = ''; }
      list[i] = n;
      return list;
    });
  };

  const summaryLine = (verb) => {
    const bits = [];
    if (counts.contacts) bits.push(`${counts.contacts} new contact${counts.contacts === 1 ? '' : 's'}`);
    if (counts.internal) bits.push(`${counts.internal} new colleague${counts.internal === 1 ? '' : 's'}`);
    if (counts.tasks) bits.push(`${counts.tasks} action item${counts.tasks === 1 ? '' : 's'}`);
    return `${verb} a Meeting on ${report.account_name}${bits.length ? `, ${bits.join(', ')}` : ''}, and links every attendee.`;
  };

  const header = (
    <header className="mr-head">
      <button type="button" className="mr-fold" onClick={() => setOpen((o) => (o === true ? false : true))} aria-expanded={!folded} aria-label={folded ? 'Expand the write-up' : 'Collapse the write-up'}>
        {folded ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>
      <FileText size={14} className="mr-icon" aria-hidden="true" />
      <span className="mr-head-title">
        <Link to={`/accounts/${report.account_id}`}>{report.account_name}</Link> {'<>'} {companyFor(report)} Meeting Summary | {longDate(report.meeting_date)}
      </span>
      {proposal.state === 'applied' && <span className="mr-state mr-state-applied"><Check size={12} aria-hidden="true" /> Saved</span>}
      {proposal.state === 'rejected' && <span className="mr-state">Discarded</span>}
      {proposal.state === 'failed' && <span className="mr-state mr-state-failed"><AlertTriangle size={12} aria-hidden="true" /> Not saved</span>}
      {pending && superseded && <span className="mr-state">Earlier draft</span>}
    </header>
  );

  if (folded) return <section className="mr-card is-folded" aria-label={proposal.title}>{header}</section>;

  return (
    <section className={`mr-card mr-${proposal.state}`} aria-label={proposal.title} role="region">
      {header}

      {pending && (
        <div className="mr-audience" role="radiogroup" aria-label="Audience">
          <button type="button" role="radio" aria-checked={report.audience !== 'customer'} className={report.audience !== 'customer' ? 'is-on' : ''} onClick={() => patch((r) => ({ ...r, audience: 'internal' }))} disabled={!canEdit}>Internal</button>
          <button type="button" role="radio" aria-checked={report.audience === 'customer'} className={report.audience === 'customer' ? 'is-on' : ''} onClick={() => patch((r) => ({ ...r, audience: 'customer' }))} disabled={!canEdit}>Customer</button>
          <span className="mr-audience-hint">{report.audience === 'customer' ? 'For the customer: no amounts, no internal remarks.' : 'For colleagues.'}</span>
        </div>
      )}
      {leak && (
        <div className="mr-warn" role="alert">
          <AlertTriangle size={13} aria-hidden="true" />
          <span>Not for a customer as written — it carries “{leak}”. Edit it out, or</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAsk('Rewrite the write-up for the customer: no amounts, margins, competitors or internal remarks; keep it short and warm.')} disabled={busy}><Sparkles size={12} aria-hidden="true" /> ask for a customer version</button>
        </div>
      )}

      <div className="mr-doc">
        <div className="mr-row"><span className="mr-label">Subject</span><InlineField value={report.subject} onSave={(v) => patch((r) => ({ ...r, subject: v }))} label="Subject" canEdit={canEdit} className="mr-subject" /></div>
        <div className="mr-row">
          <span className="mr-label">Date</span>
          <InlineField value={report.meeting_date} type="date" format={longDate} onSave={(v) => patch((r) => ({ ...r, meeting_date: v }))} label="Meeting date" canEdit={canEdit} />
          {report.opportunity_name && <span className="mr-meta">· Opportunity: <Link to={`/opportunities/${report.opportunity_id}`}>{report.opportunity_name}</Link></span>}
        </div>

        <h4 className="mr-h">{report.account_name} attendees</h4>
        <ul className="mr-chips" aria-label={`${report.account_name} attendees`}>
          {customers.map((p, i) => <Person key={p.key} person={p} side="customer" canEdit={canEdit} onEdit={editAt('customer_attendees', i)} onToggle={toggleAt('customer_attendees', i)} />)}
          {customers.length === 0 && <li className="mr-empty">No one recorded</li>}
          {canEdit && <li><Adder label="Add" placeholder="Name" onAdd={(name) => setList('customer_attendees', (l) => [...l, { key: newKey('c'), contact_id: null, name, title: '', email: '', is_new: true, include: true }])} /></li>}
        </ul>

        <h4 className="mr-h">{companyFor(report)} attendees</h4>
        <ul className="mr-chips" aria-label={`${companyFor(report)} attendees`}>
          {internals.map((p, i) => <Person key={p.key} person={p} side="internal" titled={false} canEdit={canEdit} onEdit={editAt('internal_attendees', i)} onToggle={toggleAt('internal_attendees', i)} />)}
          {internals.length === 0 && <li className="mr-empty">No one recorded</li>}
          {canEdit && <li><Adder label="Add" placeholder="Name" onAdd={(name) => setList('internal_attendees', (l) => [...l, { key: newKey('i'), user_id: null, internal_person_id: null, name, title: '', is_new: true, include: true }])} /></li>}
        </ul>

        <h4 className="mr-h">Executive summary</h4>
        <div className="mr-summary"><InlineField value={report.executive_summary} multiline onSave={(v) => patch((r) => ({ ...r, executive_summary: v }))} label="Executive summary" canEdit={canEdit} /></div>

        <h4 className="mr-h">Key highlights</h4>
        <ul className="mr-list" aria-label="Key highlights">
          {highlights.map((h, i) => (
            <li key={`h${i}`}>
              <InlineField value={h} multiline onSave={(v) => setList('highlights', (l) => { l[i] = v; return l; })} label={`Highlight ${i + 1}`} canEdit={canEdit} />
              {canEdit && <button type="button" className="mr-x" onClick={() => setList('highlights', (l) => { l.splice(i, 1); return l; })} aria-label={`Remove highlight ${i + 1}`}><X size={12} /></button>}
            </li>
          ))}
          {canEdit && <li className="mr-list-add"><Adder label="Add a highlight" placeholder="One sentence" onAdd={(v) => setList('highlights', (l) => [...l, v])} /></li>}
        </ul>

        <h4 className="mr-h">Next steps / action items</h4>
        <ol className="mr-steps" aria-label="Next steps / action items">
          {steps.map((n, i) => (
            <li key={n.key} className={n.as_task === false ? 'is-note' : ''}>
              <div className="mr-step-title"><InlineField value={n.title} onSave={(v) => editAt('next_steps', i)('title', v)} label={`Next step ${i + 1}`} canEdit={canEdit} /></div>
              <div className="mr-step-meta">
                {pending ? (
                  <select value={ownerValue(n)} onChange={setOwner(i)} disabled={!canEdit} aria-label={`Owner of next step ${i + 1}`}>
                    <option value="">You</option>
                    {users.map((u) => <option key={u.id} value={`u${u.id}`}>{u.name}</option>)}
                    {n.owner_name && !n.owner_user_id && <option value={`o:${n.owner_name}`}>{n.owner_name} (not a user)</option>}
                  </select>
                ) : <span>{n.owner_name || 'You'}</span>}
                <span className="mr-step-due">
                  <InlineField value={n.due_date || ''} type="date" format={(v) => (v ? `by ${longDate(v)}` : '')} emptyText="no date" onSave={(v) => editAt('next_steps', i)('due_date', v || null)} label={`Due date of next step ${i + 1}`} canEdit={canEdit} />
                </span>
                {pending && (
                  <label className="mr-step-task" title="Create this as an action item on approval">
                    <input type="checkbox" checked={n.as_task !== false} onChange={(e) => editAt('next_steps', i)('as_task', e.target.checked)} disabled={!canEdit} /> action item
                  </label>
                )}
                {canEdit && <button type="button" className="mr-x" onClick={removeAt('next_steps', i)} aria-label={`Remove next step ${i + 1}`}><X size={12} /></button>}
              </div>
            </li>
          ))}
          {canEdit && <li className="mr-list-add"><Adder label="Add a next step" placeholder="What, imperative" onAdd={(v) => setList('next_steps', (l) => [...l, { key: newKey('n'), title: v, owner_user_id: null, owner_name: '', due_date: null, as_task: true }])} /></li>}
        </ol>
      </div>

      {proposal.state === 'applied' && (
        <div className="mr-done">
          <Check size={13} aria-hidden="true" />
          <span>
            Saved as a Meeting on <Link to={`/accounts/${report.account_id}`}>{report.account_name}</Link>
            {proposal.result && proposal.result.created ? ` · ${proposal.result.created.contacts.length} contact${proposal.result.created.contacts.length === 1 ? '' : 's'} added · ${proposal.result.created.tasks.length} action item${proposal.result.created.tasks.length === 1 ? '' : 's'} created` : ''}.
          </span>
        </div>
      )}
      {proposal.result && proposal.result.errors && proposal.result.errors.length > 0 && (
        <ul className="mr-errors" aria-label="What could not be saved">
          {proposal.result.errors.map((e, i) => <li key={i}><AlertTriangle size={12} aria-hidden="true" /> {e.name || e.title || e.step}: {e.message}</li>)}
        </ul>
      )}
      {proposal.state === 'failed' && !proposal.result && <div className="alert alert-error asst-error" role="alert">{proposal.error}</div>}

      <footer className="mr-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={copy} disabled={!!leak}><Copy size={13} aria-hidden="true" /> Copy for email</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={copySubject}>Copy subject</button>
        {(pending || proposal.state === 'failed') && (
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={approve} disabled={!canEdit || !!leak || (proposal.state === 'failed' && working)} title={summaryLine('Saves')}>
              {working ? <Loader2 size={13} className="asst-spin" aria-hidden="true" /> : <Check size={13} aria-hidden="true" />} {proposal.state === 'failed' ? 'Try again' : 'Approve'}
            </button>
            {pending && <button type="button" className="btn btn-secondary btn-sm" onClick={onDiscard} disabled={!canEdit}>Discard</button>}
          </>
        )}
      </footer>
      {pending && !superseded && (
        <div className="mr-revise" aria-label="Ask for changes">
          <Sparkles size={12} aria-hidden="true" />
          <span>Not right yet? Ask for changes in your own words below, as many rounds as you like; a revised draft replaces this card.</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAsk('Make the executive summary shorter: one paragraph, keeping every fact, name and next step.')} disabled={busy}>Shorter</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAsk('Expand the meeting summary: more detail in the executive summary and the highlights, from what I told you only, nothing invented.')} disabled={busy}>More detail</button>
          {report.audience !== 'customer' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAsk('Rewrite the write-up for the customer: no amounts, margins, competitors or internal remarks; keep it short and warm.')} disabled={busy}>Customer version</button>}
        </div>
      )}
      {pending && <p className="mr-foot">{summaryLine('Approving saves')} Copy first if you are emailing it.</p>}
    </section>
  );
}
